import { FC, useMemo } from "react";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import Autocomplete from "@mui/material/Autocomplete";
import { ICustomField } from "../../@types/customField.type";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { mapFieldTypeOptionsValue } from "../../utils/mapFieldTypeOptions";
import styles from "./styles.module.scss";

interface OptionsDynimicFieldsProps {
  dynamicFields: ICustomField[];
  formDataCustomFields: {
    [x: string]: any;
  };
  setFormDataCustomFields: React.Dispatch<
    React.SetStateAction<{
      [x: string]: any;
    }>
  >;
  errorsFormDataCustomFields: any;
}

export const OptionsDynimicFields: FC<OptionsDynimicFieldsProps> = ({
  dynamicFields,
  setFormDataCustomFields,
  formDataCustomFields,
  errorsFormDataCustomFields,
}) => {
  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }
    >
  ) => {
    const { name, value } = event.target;
    setFormDataCustomFields((prev) => ({
      ...prev,
      [name!]: value,
    }));
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormDataCustomFields((prev) => ({
      ...prev,
      [name!]: checked ? "Sim" : "Não",
    }));
  };

  const handleDateChange = (name: string, value: Dayjs | null) => {
    setFormDataCustomFields((prev) => ({
      ...prev,
      [name]: value ? value.format("YYYY-MM-DD") : "",
    }));
  };

  const handleAutocompleteChange = (name: string, value: string | null) => {
    setFormDataCustomFields((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  };

  const typePriority: Record<string, number> = {
    texto: 1,
    lista: 2,
    data: 3,
    booleano: 4,
    textoMultlinhas: 5,
  };

  const sortedCustomFields = useMemo(() => {
    return [...dynamicFields].sort((a, b) => {
      const priorityA =
        typePriority[mapFieldTypeOptionsValue[a.customFieldTypeId]] || 999;
      const priorityB =
        typePriority[mapFieldTypeOptionsValue[b.customFieldTypeId]] || 999;
      return priorityA - priorityB;
    }) as ICustomField[];
  }, [dynamicFields]);

  return (
    <div className={styles.customFieldsContainer}>
      <div className={styles.fields}>
        {sortedCustomFields?.map((field) => {
          if (mapFieldTypeOptionsValue[field.customFieldTypeId] === "texto") {
            return (
              <TextField
                key={field.id}
                name={field.name}
                label={field.name}
                value={formDataCustomFields[field.name] || ""}
                onChange={handleChange}
                placeholder="Digite..."
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errorsFormDataCustomFields[field.name]}
                helperText={errorsFormDataCustomFields[field.name]}
              />
            );
          }

          if (
            mapFieldTypeOptionsValue[field.customFieldTypeId] === "booleano"
          ) {
            return (
              <div
                className={styles.fieldSwitch}
                key={field.id}
                style={{ margin: "16px 0" }}
              >
                <label>
                  <Switch
                    name={field.name}
                    checked={
                      formDataCustomFields[field.name] === "Sim" ? true : false
                    }
                    onChange={handleSwitchChange}
                  />
                  {field.name}
                </label>
              </div>
            );
          }

          if (
            mapFieldTypeOptionsValue[field.customFieldTypeId] === "lista" &&
            field.listOptions
          ) {
            return (
              <Autocomplete
                key={field.id}
                options={field.listOptions.split(",")}
                value={formDataCustomFields[field.name] || null}
                onChange={(_, value) =>
                  handleAutocompleteChange(field.name, value)
                }
                noOptionsText="Sem opções"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione..."
                    label={field.name}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errorsFormDataCustomFields[field.name]}
                    helperText={errorsFormDataCustomFields[field.name]}
                  />
                )}
              />
            );
          }

          if (mapFieldTypeOptionsValue[field.customFieldTypeId] === "data") {
            return (
              <DatePicker
                key={field.id}
                label={field.name}
                value={
                  formDataCustomFields[field.name]
                    ? dayjs(formDataCustomFields[field.name])
                    : null
                }
                onChange={(value) => handleDateChange(field.name, value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputLabelProps: { shrink: true },
                    error: !!errorsFormDataCustomFields[field.name],
                    helperText: errorsFormDataCustomFields[field.name],
                  },
                }}
              />
            );
          }

          if (
            mapFieldTypeOptionsValue[field.customFieldTypeId] ===
            "textoMultlinhas"
          ) {
            return (
              <TextField
                className={styles.fieldMultiline}
                key={field.id}
                name={field.name}
                label={field.name}
                value={formDataCustomFields[field.name] || ""}
                onChange={handleChange}
                fullWidth
                placeholder="Digite..."
                multiline
                rows={4}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errorsFormDataCustomFields[field.name]}
                helperText={errorsFormDataCustomFields[field.name]}
              />
            );
          }

          return null;
        })}
      </div>
    </div>
  );
};
